import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import * as TeamsState from '../../store/TeamsState';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Contact } from '../../interfaces/Contact';
import { ContactsActionCreators } from '../../store/contacts';
import { Customer } from '../../interfaces/Customer';
import { RelationShipTypes } from '../../interfaces/ContactCustomerRole';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Textarea, Option, Dropdown, useToastController, Toast, ToastTitle, ToastTrigger, Link, InputOnChangeData, TextareaOnChangeData } from '@fluentui/react-components';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { useCustomers } from '../Tools/ObjectLoader';

function isValidEmail(email: string) {
    return /\S+@\S+\.\S+/.test(email);
}

export const ContactEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const contactsEdit = useSelector<ApplicationState, Contact | null>((state: ApplicationState) => state.contactsState.contactsEdit);
    const { customers } = useCustomers();
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [title, setTitle] = React.useState(contactsEdit ? contactsEdit.title : "");
    const [firstName, setFirstName] = React.useState(contactsEdit ? contactsEdit.firstName : "");
    const [lastName, setLastName] = React.useState(contactsEdit ? contactsEdit.lastName : "");
    const [email, setEmail] = React.useState(contactsEdit ? contactsEdit.email : null);
    const [phone, setPhone] = React.useState(contactsEdit ? contactsEdit.phone : null);
    const [mobile, setMobile] = React.useState(contactsEdit ? contactsEdit.mobile : null);
    const [description, setDescription] = React.useState(contactsEdit ? contactsEdit.description : null);
    const [defaultLanguage, setDefaultLanguage] = React.useState(contactsEdit ? contactsEdit.defaultLanguage : null);
    const [customer, setCustomer] = React.useState<Customer | undefined | null>(null);
    const [role, setRole] = React.useState<RelationShipTypes>("MEMBER");
    const [linkedIn, setLinkedIn] = React.useState(contactsEdit ? contactsEdit.linkedIn : null);
    // Fields validation
    const [isFirstNameValid, setFirstNameValid] = React.useState(false);
    const [isLastNameValid, setLastNameValid] = React.useState(false);
    const [isEmailValid, setEmailValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    React.useEffect(() => {
        setFirstNameValid(firstName !== undefined && firstName !== null && firstName.length > 0);
    }, [firstName]);
    React.useEffect(() => {
        setLastNameValid(lastName !== undefined && lastName !== null && lastName.length > 0);
    }, [lastName]);
    React.useEffect(() => {
        setEmailValid(email !== undefined && email !== null && email.length > 0 && isValidEmail(email));
    }, [email]);
    React.useEffect(() => {
        let submitValid = isFirstNameValid && isLastNameValid && isEmailValid;
        if (submitValid && contactsEdit) {
            // If valid, check if any change on existing item
            submitValid = submitValid && (
                (firstName !== contactsEdit.firstName)
                || (lastName !== contactsEdit.lastName)
                || (email !== contactsEdit.email)
                || (title !== contactsEdit.title)
                || (phone !== contactsEdit.phone)
                || (mobile !== contactsEdit.mobile)
                || (description !== contactsEdit.description)
                || (linkedIn !== contactsEdit.linkedIn)
                || (defaultLanguage !== contactsEdit.defaultLanguage)
            );
        }
        setSubmitValid(submitValid);
    }, [isFirstNameValid, isLastNameValid, isEmailValid, title, firstName, lastName, email, phone, mobile, description, linkedIn, defaultLanguage]);

    const submit = () => {
        const c: Contact = {
            contactid: contactsEdit && contactsEdit.contactid ? contactsEdit.contactid : null,
            title: title,
            firstName: firstName,
            lastName: lastName,
            email: email,
            phone: phone,
            mobile: mobile,
            description: description,
            defaultLanguage: defaultLanguage,
            linkedIn: linkedIn,
            contactCustomerRoleLoading: false,
            contactCustomerRole: null,
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                Saving contact...
                            </ToastTitle>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/Contacts/" + (contactsEdit && contactsEdit.contactid !== null ? contactsEdit.contactid : ""), {
                        method: contactsEdit && contactsEdit.contactid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Contact saved
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<Contact>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Contact save failed
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) dispatch(ContactsActionCreators.patchContactEdition(data));
                            if (customer && data && data.contactid) {
                                fetch("api/Contacts/" + data.contactid + "/ContactCustomerRoles", {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                        authorization: "bearer " + token,
                                    },
                                    body: JSON.stringify([{ customerid: customer.customerid, relationShipTypeId: role }]),
                                    mode: "cors",
                                    cache: "default",
                                })
                                    .then(response => {
                                        if (response.status === 200) {
                                            console.log(response);
                                        }
                                    });
                            }
                            dispatch(ContactsActionCreators.requestContactEdition(null));
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{contactsEdit && contactsEdit.contactid !== null ? "Edit contact " + contactsEdit.firstName + " " + contactsEdit.lastName : "Create new consultant"}</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(ContactsActionCreators.requestContactEdition(null));
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        <Field label="Title" className={styles.fluid}>
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setTitle(data.value); }}
                                value={title ? title : undefined}
                                maxLength={50}
                            />
                        </Field>
                        <Field label="First name" className={styles.fluid} required
                        >
                            {/*    error={!isFirstNameValid}*/}
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setFirstName(data.value); }}
                                value={firstName ? firstName : undefined}
                                maxLength={100}
                            />
                        </Field>
                        <Field label="Last name" className={styles.fluid} required
                        >
                            {/*    error={!isLastNameValid}*/}
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setLastName(data.value); }}
                                value={lastName ? lastName : undefined}
                                maxLength={100}
                            />
                        </Field>
                        <Field label="Email" className={styles.fluid} required
                        >
                            {/*    error={!isEmailValid}*/}
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setEmail(data.value); }}
                                value={email ? email : undefined}
                                maxLength={100}
                            />
                        </Field>
                        <Field label="Phone" className={styles.fluid}>
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setPhone(data.value); }}
                                value={phone ? phone : undefined}
                                maxLength={50}
                            />
                        </Field>
                        <Field label="Mobile" className={styles.fluid}>
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setMobile(data.value); }}
                                value={mobile ? mobile : undefined}
                                maxLength={50}
                            />
                        </Field>
                        {contactsEdit && contactsEdit.contactid === null &&
                            <Field label={t('Customer')} className={styles.fluid}>
                                <div className={styles.onSingleLine}>
                                    <div className={styles.field50}>
                                        <CustomerDropdown currentCustomer={customer ?? undefined}
                                            customers={customers ?? []}
                                            setCustomer={setCustomer} />
                                    </div>
                                    <div className={styles.field50}>
                                        <Dropdown
                                            className={styles.fluid}
                                            onOptionSelect={(event, data) => {
                                                setRole(data.optionValue ? data.optionValue as RelationShipTypes : "MEMBER");
                                            }}
                                            value={t(role)}
                                            selectedOptions={[t(role)]}
                                        >
                                            <Option key="OWNER" value="OWNER">{t("OWNER")}</Option>
                                            <Option key="MEMBER" value="MEMBER">{t("MEMBER")}</Option>
                                            <Option key="FREELANCE" value="FREELANCE">{t("FREELANCE")}</Option>
                                            <Option key="PROJECTMGR" value="PROJECTMGR">{t("PROJECTMGR")}</Option>
                                            <Option key="CONTRACTMGR" value="CONTRACTMGR">{t("CONTRACTMGR")}</Option>
                                            <Option key="SALESMGR" value="SALESMGR">{t("SALESMGR")}</Option>
                                            <Option key="INVOICE" value="INVOICE">{t("INVOICE")}</Option>
                                        </Dropdown>
                                    </div>
                                </div>
                            </Field>
                        }
                        <Field label="LinkedIn" className={styles.fluid}>
                            <Input
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => { setLinkedIn(data.value); }}
                                value={linkedIn ? linkedIn : undefined}
                            />
                        </Field>
                        <Field label="Description" className={styles.fluid}>
                            <Textarea resize="vertical"
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => { setDescription(data.value); }}
                                value={description ? description : undefined}
                            />
                        </Field>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(ContactsActionCreators.requestContactEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};